<script lang="ts">
  import { demoStats, demoEvents, resetKey, type TesterRow, type TesterEvent } from '../lib/demoAdmin';

  let rows = $state<TesterRow[]>([]);
  let loading = $state(true);
  let error = $state('');
  let busyKey = $state('');                          // key being reset
  let openKey = $state('');                          // key whose events are expanded
  let events = $state<TesterEvent[]>([]);
  let eventsLoading = $state(false);

  async function load() {
    loading = true; error = '';
    try { rows = await demoStats(); }
    catch (e) { error = (e as Error)?.message || String(e); rows = []; }
    finally { loading = false; }
  }
  $effect(() => { load(); });

  const totals = $derived({
    testers: rows.length,
    active: rows.filter((r) => r.lastSeen > 0).length,
    tokens: rows.reduce((s, r) => s + (r.tokensTotal || 0), 0),
    calls: rows.reduce((s, r) => s + (r.calls || 0), 0),
  });

  async function doReset(key: string) {
    if (!confirm(`Reset ${key}? Its token counter goes back to 0 so the tester can keep chatting.`)) return;
    busyKey = key;
    try { await resetKey(key); await load(); }
    catch (e) { alert(`Reset failed: ${(e as Error)?.message ?? e}`); }
    finally { busyKey = ''; }
  }
  async function toggleEvents(key: string) {
    if (openKey === key) { openKey = ''; return; }
    openKey = key; events = []; eventsLoading = true;
    try { events = await demoEvents(key); }
    catch (e) { events = [{ event: 'error', detail: (e as Error)?.message || String(e), ip: '', ts: 0, userAgent: '' }]; }
    finally { eventsLoading = false; }
  }

  function fmtWhen(ms: number): string {
    if (!ms) return 'never';
    const s = Math.floor((Date.now() - ms) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(ms).toLocaleDateString();
  }
  function fmtDateTime(ms: number): string { return ms ? new Date(ms).toLocaleString() : '—'; }
  function fmtK(n: number): string {
    if (n >= 1000) { const k = n / 1000; return (k >= 100 ? String(Math.round(k)) : k.toFixed(1).replace(/\.0$/, '')) + 'k'; }
    return String(Math.round(n));
  }
  const pct = (r: TesterRow) => (r.cap ? Math.min(100, Math.round((r.tokensTotal / r.cap) * 100)) : 0);
</script>

<div class="body">
  <div class="head">
    <div>
      <h1>Demo testers</h1>
      <p class="muted">Everyone using a <code>/demo</code> passkey — their activity, where they signed in from, and their assistant token budget.</p>
    </div>
    <button class="sync" onclick={load} disabled={loading}>{loading ? 'Loading…' : '⟳ Refresh'}</button>
  </div>

  {#if error}
    <div class="banner err"><strong>Couldn't load tester data.</strong> {error}</div>
  {/if}

  <div class="stats">
    <span class="stat"><b class="num">{totals.testers}</b> passkeys used</span>
    <span class="stat ok"><b class="num">{totals.active}</b> active</span>
    <span class="stat"><b class="num">{fmtK(totals.tokens)}</b> tokens total</span>
    <span class="stat"><b class="num">{totals.calls}</b> chat calls</span>
  </div>

  <div class="tablewrap">
    <table>
      <thead>
        <tr><th>Key</th><th>Last seen</th><th>Activity</th><th>Location (IP)</th><th>Assistant tokens</th><th class="ta-r">Actions</th></tr>
      </thead>
      <tbody>
        {#if loading}
          <tr><td colspan="6" class="empty">Loading…</td></tr>
        {:else if rows.length === 0}
          <tr><td colspan="6" class="empty">No testers yet — nobody has entered a demo key.</td></tr>
        {:else}
          {#each rows as r (r.key)}
            <tr>
              <td><span class="mono key">{r.key}</span></td>
              <td><span class="when" title={fmtDateTime(r.lastSeen)}>{fmtWhen(r.lastSeen)}</span>{#if r.lastEvent}<span class="lastev">{r.lastEvent}</span>{/if}</td>
              <td><span class="num">{r.eventCount}</span> <span class="faint">events</span></td>
              <td>
                <span class="mono ip">{r.lastIp || '—'}</span>
                {#if r.ips.length > 1}<span class="ipcount" title={r.ips.join(', ')}>+{r.ips.length - 1}</span>{/if}
              </td>
              <td>
                <div class="tok">
                  <div class="bar"><div class="fill {pct(r) >= 100 ? 'over' : pct(r) >= 80 ? 'warn' : ''}" style="width:{pct(r)}%"></div></div>
                  <span class="toknum num">{fmtK(r.tokensTotal)} / {fmtK(r.cap)}</span>
                </div>
              </td>
              <td class="ta-r">
                <div class="rowacts">
                  <button class="act" onclick={() => toggleEvents(r.key)}>{openKey === r.key ? 'Hide' : 'Events'}</button>
                  <button class="act danger" onclick={() => doReset(r.key)} disabled={busyKey === r.key}>{busyKey === r.key ? '…' : 'Reset'}</button>
                </div>
              </td>
            </tr>
            {#if openKey === r.key}
              <tr class="evrow">
                <td colspan="6">
                  {#if eventsLoading}
                    <div class="evempty">Loading events…</div>
                  {:else if events.length === 0}
                    <div class="evempty">No events logged for {r.key}.</div>
                  {:else}
                    <div class="evlist">
                      {#each events as e (e.ts + e.event)}
                        <div class="ev">
                          <span class="ev-t">{fmtDateTime(e.ts)}</span>
                          <span class="ev-e">{e.event}</span>
                          {#if e.detail}<span class="ev-d">{e.detail}</span>{/if}
                          <span class="mono ev-ip">{e.ip || '—'}</span>
                        </div>
                      {/each}
                    </div>
                  {/if}
                </td>
              </tr>
            {/if}
          {/each}
        {/if}
      </tbody>
    </table>
  </div>

  <p class="footnote">Token budget is per key (default 100k). <b>Reset</b> zeroes the counter so the tester can keep chatting. IPs are captured at each logged action for a rough location trail.</p>
</div>

<style>
  .body { width: 100%; padding: var(--space-6) var(--space-8); display: flex; flex-direction: column; gap: var(--space-4); }
  .head { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; }
  h1 { font-weight: var(--fw-heading); letter-spacing: -0.01em; }
  .muted { color: var(--text-3); font-size: var(--fs-body); margin-top: 2px; }
  code { font-family: var(--mono); font-size: 0.92em; color: var(--accent); background: var(--accent-soft); padding: 1px 5px; border-radius: 5px; }
  .sync { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-xs); }
  .sync:hover:not(:disabled) { color: var(--accent); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }
  .sync:disabled { opacity: 0.6; cursor: default; }

  .banner { padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); font-size: var(--fs-xs); }
  .banner.err { color: var(--text-2); background: var(--surface); border: 1px solid var(--line-strong); }

  .stats { display: flex; flex-wrap: wrap; gap: var(--space-2) var(--space-4); }
  .stat { font-size: var(--fs-xs); color: var(--text-3); }
  .stat b { color: var(--text); margin-right: 4px; font-size: var(--fs-heading); }
  .stat.ok b { color: var(--ok); }

  .tablewrap { border: 1px solid var(--line); border-radius: var(--radius-md); overflow-x: auto; background: var(--surface); }
  table { width: 100%; border-collapse: collapse; min-width: 760px; }
  th, td { text-align: left; padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--line); vertical-align: middle; }
  thead th { font-size: var(--fs-2xs); letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-3); background: var(--surface-2); }
  tbody tr:last-child td { border-bottom: none; }
  .ta-r { text-align: right; }
  .empty { text-align: center; color: var(--text-3); padding: var(--space-8); }

  .mono { font-family: var(--mono); }
  .key { font-weight: 700; letter-spacing: 0.08em; color: var(--accent-2); }
  .when { display: block; }
  .lastev { display: block; font-size: var(--fs-2xs); color: var(--text-3); }
  .ip { font-size: var(--fs-xs); color: var(--text-2); }
  .ipcount { margin-left: 6px; font-size: var(--fs-2xs); color: var(--text-3); border: 1px solid var(--line-strong); border-radius: var(--radius-pill); padding: 0 6px; cursor: help; }

  .tok { display: flex; flex-direction: column; gap: 4px; min-width: 130px; }
  .bar { height: 6px; border-radius: var(--radius-pill); background: var(--surface-3); overflow: hidden; }
  .fill { height: 100%; background: var(--accent); border-radius: var(--radius-pill); }
  .fill.warn { background: var(--warn); } .fill.over { background: var(--danger); }
  .toknum { font-size: var(--fs-2xs); color: var(--text-3); }

  .rowacts { display: inline-flex; gap: var(--space-2); justify-content: flex-end; }
  .act { font-size: var(--fs-2xs); font-weight: 700; padding: var(--space-1) var(--space-3); border-radius: var(--radius-pill); border: 1px solid var(--line-strong); color: var(--text-2); background: var(--surface-2); }
  .act:hover:not(:disabled) { color: var(--accent); border-color: var(--accent); }
  .act.danger:hover:not(:disabled) { color: var(--danger); border-color: var(--danger); }
  .act:disabled { opacity: 0.5; cursor: default; }

  .evrow td { background: var(--bg); padding-top: 0; }
  .evempty { color: var(--text-3); padding: var(--space-3) 0; font-size: var(--fs-xs); }
  .evlist { display: flex; flex-direction: column; gap: 2px; padding: var(--space-2) 0; max-height: 280px; overflow-y: auto; }
  .ev { display: flex; align-items: baseline; gap: var(--space-3); font-size: var(--fs-2xs); padding: 3px 0; border-bottom: 1px solid var(--line); }
  .ev-t { color: var(--text-3); min-width: 150px; }
  .ev-e { color: var(--accent-2); font-weight: 700; min-width: 90px; }
  .ev-d { color: var(--text-2); flex: 1; }
  .ev-ip { color: var(--text-3); }

  .footnote { color: var(--text-3); font-size: var(--fs-2xs); line-height: 1.6; }
  .footnote b { color: var(--text-2); }
</style>
